import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Loader2, User, Bot, MessageCircle } from 'lucide-react';
import { useGroupMessages } from '../hooks/use-promotion-groups';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface GroupMessagesModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupJid: string | null;
  groupName: string;
}

export function GroupMessagesModal({
  isOpen,
  onClose,
  groupJid,
  groupName,
}: GroupMessagesModalProps) {
  const { data, isLoading, error } = useGroupMessages(groupJid, isOpen);

  const messages = data?.data || [];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Histórico de Mensagens - {groupName}
            </div>
          </DialogTitle>
          <DialogDescription>
            {messages.length > 0
              ? `${messages.length} mensagem(ns) encontrada(s)`
              : 'Nenhuma mensagem encontrada'}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-3 overflow-y-auto max-h-[60vh] pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <p>Erro ao carregar mensagens. Tente novamente.</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <MessageCircle className="h-12 w-12 mb-3 opacity-50" />
              <p>Nenhuma mensagem neste grupo ainda.</p>
            </div>
          ) : (
            messages.map((message) => (
              <div
                key={message.id}
                className="flex gap-3 p-4 rounded-lg border bg-gray-50 dark:bg-gray-900/10 border-gray-200 dark:border-gray-800"
              >
                <div className="flex-shrink-0">
                  <div className="w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center">
                    <User className="h-4 w-4 text-white" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-sm">
                      {message.senderName || 'Sem nome'}
                    </span>

                    {/* Status Badge */}
                    {message.processingStatus === 'PENDING' && (
                      <Badge variant="warning" className="text-xs">
                        Pendente
                      </Badge>
                    )}
                    {message.processingStatus === 'PROCESSING' && (
                      <Badge variant="info" className="text-xs">
                        Processando
                      </Badge>
                    )}
                    {message.processingStatus === 'PROCESSED' && (
                      <Badge variant="success" className="text-xs">
                        Processado
                      </Badge>
                    )}
                    {message.processingStatus === 'SENT' && (
                      <Badge variant="success" className="text-xs">
                        Enviado
                      </Badge>
                    )}
                    {message.processingStatus === 'FAILED' && (
                      <Badge variant="destructive" className="text-xs">
                        Erro
                      </Badge>
                    )}
                    {message.processingStatus === 'IGNORED' && (
                      <Badge variant="default" className="text-xs">
                        Ignorado
                      </Badge>
                    )}

                    <span className="text-xs text-muted-foreground ml-auto">
                      {formatDistanceToNow(new Date(message.timestamp), {
                        addSuffix: true,
                        locale: ptBR,
                      })}
                    </span>
                  </div>

                  <p className="text-sm whitespace-pre-wrap break-words mb-2">{message.messageText}</p>

                  {/* Resposta da IA */}
                  {message.aiResponse && (
                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-1 mb-1">
                        <Bot className="h-3 w-3 text-blue-600" />
                        <span className="text-xs font-semibold text-blue-600">Resposta da IA:</span>
                      </div>
                      <p className="text-xs whitespace-pre-wrap break-words">{message.aiResponse}</p>
                    </div>
                  )}

                  {/* Erro */}
                  {message.processingError && (
                    <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                      <span className="text-xs text-red-600">{message.processingError}</span>
                    </div>
                  )}

                  {/* Informações adicionais */}
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {message.hasMedia && (
                      <Badge variant="outline" className="text-xs">
                        📎 {message.mediaType || 'Mídia'}
                      </Badge>
                    )}
                    {message.destinationGroupJid && (
                      <Badge variant="outline" className="text-xs">
                        ✓ Enviado para destino
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
